/**
 * Table detection for the ASK AI CSV download feature.
 *
 * One pure function over the host element that holds a rendered answer. The
 * detection effect in `AnswerTableDownloads.tsx` calls it after each commit to
 * learn which tables an answer contains and in what order; the position of a
 * table in the returned array is that table's zero-based position index
 * (Req 1.3), which `buildCsvFileName.ts` puts in the file name.
 *
 * No React, no state, no side effects: the host element and the tables it
 * contains are read and never mutated.
 */

/**
 * Outermost `table` elements inside a rendered answer, in document order.
 *
 * `querySelectorAll` returns matches in document order, so the caller can take
 * each table's zero-based position index from its array position (Req 1.3).
 * Every `table` element in the rendered content is a candidate (Req 1.1), and a
 * table produced from GitHub Flavored Markdown pipe syntax is indistinguishable
 * from one written as literal HTML by the time it reaches the DOM — which is
 * why detection is DOM-based and needs no special case for either (Req 1.2).
 *
 * A table nested inside another table is dropped, so only the outermost table
 * is reported (Req 1.4). The nesting test starts from `parentElement` rather
 * than from the table itself, because `closest` matches the starting element:
 * starting from the table would find the table itself and exclude every table.
 *
 * @param host Element holding the rendered answer content.
 * @returns The outermost tables, in document order. Empty when the answer holds
 *   no table, which is what makes an answer render with no download controls
 *   and no download offer (Req 1.5).
 */
export function detectAnswerTables(host: HTMLElement): HTMLTableElement[] {
  return Array.from(host.querySelectorAll("table")).filter((table) => {
    const ancestorTable = table.parentElement?.closest("table") ?? null;
    return ancestorTable === null;
  });
}
